import React, { useMemo, useState, useEffect } from "react";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import {
  Card,
  Table,
  Button,
  Typography,
  Alert,
  Tag,
} from "antd";
import type { ColumnType } from "antd/es/table";
import { ArrowRightOutlined, DownloadOutlined, SettingOutlined } from "@ant-design/icons";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useTender } from "../../contexts/TenderContext";
import { useDevMode } from "@/contexts/DevModeContext";
import { useNavigate } from "react-router-dom";
import { COLORS, formatUnit } from "../../utils/constants";
import { formatCurrency } from "../../utils/calculations";
import { useItemLibrary } from "../../hooks/useItemLibrary";
import MainLayout from "../layout/MainLayout";
import NumberInput from "../common/NumberInput";
import ExpandCollapseAllButton from "../common/ExpandCollapseAllButton";
import { useTableRenderDeferral } from "../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../common/TableLoadingPlaceholder";
import { ExcelImportModal } from "../common/ExcelImportExport";
import { ColumnConfigModal, ConfigSection, ConfigField, selectStyles } from "../common/ColumnConfigModal";
import type { FoulBuildUpRowData, SubstationBaseConfig } from "../../types/tender.types";
import { MAIN_SERVICE_TRENCHING_MAPPING } from "../../utils/mainServiceTrenchingMapping";
import { parseMainServicesTrenching } from "../../utils/roadsExcelUtils";
import {
  SUBSTATION_BASE_ROW_DEFS,
  SubstationBaseRowType,
} from "../../utils/substationBaseRowDefs";
import { parseSubstationBaseFromExcel } from "../../utils/substationBaseExcelUtils";
import { findMainServiceTrenchingEvaluateCode } from "../../utils/mainServicesEvaluateCodesMapping";
import { findSubstationBaseEvaluateCode } from "../../utils/substationBaseEvaluateCodesMapping";

const { Title, Text } = Typography;

const DEFAULT_CART_SPOIL_DISTANCE_M = 0;
const DEFAULT_SSB_BLINDING_MM = 0;
const DEFAULT_SSB_FORMWORK_PERIMETER_M = 0;
const DEFAULT_SSB_FORMWORK_PIT_M = 0;
const DEFAULT_SSB_FORMWORK_VOID_M = 0;
const DEFAULT_SSB_MESH_TYPE = '';
const DEFAULT_SSA_BASE_COURSE = '';
const DEFAULT_SSA_BINDER_COURSE = '';
const DEFAULT_SSA_SURFACE_COURSE = '';

// Same option list as Compound.tsx/Foundations' "Mesh Reinforcement" dropdown.
const MESH_TYPE_OPTIONS = ['A142', 'A193', 'A252', 'A393', 'B283', 'B385', 'B503', 'B785', 'B1131'];

// SUB STATION ACCESS row ssa_2 ("XXXmm AC32 DBM base course") — same
// dropdown-of-full-labels idea, and same option list, as Compound.tsx's Sales
// Parking "xxmm Base Course".
const SSA_BASE_COURSE_OPTIONS = ['100mm thick AC 32mm dense bin', '150mm thick AC 32mm dense bin'];

// SUB STATION ACCESS row ssa_3 ("XXmm AC20 DBM binder course") — same idea,
// same option list as Compound.tsx's Sales Parking "xxmm Binder Course"
// (itself Compound's Carpark Bindercourse label set).
const SSA_BINDER_COURSE_OPTIONS = [
  '50mm thick AC 20mm dense bin',
  '60mm thick AC 20mm dense bin',
  '75mm thick AC 20mm dense bin',
  '80mm thick AC 20mm dense bin',
  '100mm thick AC 20mm dense bin',
];

// SUB STATION ACCESS row ssa_6 ("XXmm AC6 DBM Surface course") — same
// dropdown-of-full-labels idea, and same option list, as Compound.tsx's Sales
// Parking "xxmm Surface Course".
const SSA_SURFACE_COURSE_OPTIONS = [
  '20mm thick AC 6mm dense surf',
  '25mm thick AC 6mm dense surf',
  '25mm thick AC 10mm dense surf',
  '30mm thick AC 6mm dense surf',
  '30mm thick AC 10mm dense surf',
];

// ── Main Services row shapes ─────────────────────────────────────────────────
interface SectionRow {
  key: string;
  rowType: 'section';
  description: string;
  ref: string;
  unit: string;
  id: string;
  children?: ItemRow[];
}

interface ItemRow {
  key: string;
  rowType: 'item';
  id: string;
  ref: string;
  description: string;
  unit: string;
}

type TableRow = SectionRow | ItemRow;
type MSColumn = ColumnType<TableRow>;

let msItemCounter = 0;
const ALL_ITEMS: ItemRow[] = MAIN_SERVICE_TRENCHING_MAPPING.flatMap((group) =>
  group.items.map((item): ItemRow => ({
    key: `item-${item.id}`,
    rowType: 'item',
    id: item.id,
    ref: item.ref ?? String(++msItemCounter),
    description: item.description,
    unit: item.unit,
  })),
);

const SECTION_HEADER_ROW: SectionRow = {
  key: 'section-main-service-trenching',
  rowType: 'section',
  id: 'section-main-service-trenching',
  ref: '',
  description: 'MAIN SERVICE TRENCHING',
  unit: '',
};

const MS_TREE_DATA: TableRow[] = [{ ...SECTION_HEADER_ROW, children: ALL_ITEMS }];
const MS_EXPAND_KEY: React.Key = SECTION_HEADER_ROW.key;

// ── Substation Base tree ─────────────────────────────────────────────────────
interface SubstationTreeNode {
  key: string;
  id: string;
  rowType: SubstationBaseRowType;
  description: string;
  unit: string;
  itemNo: number | null;
  children?: SubstationTreeNode[];
}

const buildSubstationTree = (): { tree: SubstationTreeNode[]; allExpandKeys: React.Key[] } => {
  const tree: SubstationTreeNode[] = [];
  const allExpandKeys: React.Key[] = [];
  let currentSection: SubstationTreeNode | null = null;

  for (const row of SUBSTATION_BASE_ROW_DEFS) {
    if (row.rowType === 'sectionHeader') {
      const node: SubstationTreeNode = {
        key: row.id, id: row.id, rowType: row.rowType,
        description: row.description, unit: row.unit, itemNo: row.itemNo, children: [],
      };
      tree.push(node);
      allExpandKeys.push(row.id);
      currentSection = node;
    } else {
      const node: SubstationTreeNode = {
        key: row.id, id: row.id, rowType: row.rowType,
        description: row.description, unit: row.unit, itemNo: row.itemNo,
      };
      currentSection?.children?.push(node);
    }
  }

  return { tree, allExpandKeys };
};

const { tree: SUBSTATION_TREE, allExpandKeys: SUBSTATION_EXPAND_KEYS } = buildSubstationTree();

// ── Component ────────────────────────────────────────────────────────────────
const MainServices: React.FC = () => {
  const showDevMode = useDevMode();
  useScrollToTop({ behavior: 'smooth', delay: 100 });
  const { state, updateField, saveTender } = useTender();

  // Item library for dev-mode rate lookups by evaluate code — only fetched
  // when the dev-only Evaluate Code column is actually shown.
  const { data: itemLibraryData } = useItemLibrary(state.region || "", undefined, {
    enabled: showDevMode,
  });
  const resourceCodeMap = useMemo(() => {
    const map = new Map<string, number>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, item.nettRate || item.baseRate || 0);
    });
    return map;
  }, [itemLibraryData]);
  const getLibRate = (code?: string): number | undefined => {
    if (!code) return undefined;
    return resourceCodeMap.get(code.trim().toUpperCase());
  };
  const navigate = useNavigate();
  useAutoSave();

  // Main Services expand state
  const [msExpandedKeys, setMsExpandedKeys] = useState<React.Key[]>([]);

  // Substation Base expand state
  const [sbExpandedKeys, setSbExpandedKeys] = useState<React.Key[]>([]);

  // Sticky table offset — recalculate whenever the active tab changes
  const [stickyOffset, setStickyOffset] = useState(310);

  // Import modal states
  const [msImportVisible, setMsImportVisible] = useState(false);
  const [msImportLoading, setMsImportLoading] = useState(false);
  const [sbImportVisible, setSbImportVisible] = useState(false);
  const [sbImportLoading, setSbImportLoading] = useState(false);

  // ── Main Services data ─────────────────────────────────────────────────────
  const roads = useMemo(
    () => state.roads?.carriagewayFootways.table ?? [],
    [state.roads?.carriagewayFootways.table],
  );

  const savedQuantities = useMemo(
    () => state.mainServices?.table ?? {},
    [state.mainServices?.table],
  );

  const getQuantity = (itemId: string, roadId: string): number =>
    savedQuantities[itemId]?.[roadId] ?? 0;

  const updateQuantity = (itemId: string, roadId: string, value: number) => {
    const current = state.mainServices?.table ?? {};
    const itemMap = { ...(current[itemId] ?? {}) };

    if (value === 0) {
      delete itemMap[roadId];
    } else {
      itemMap[roadId] = value;
    }

    const next: Record<string, Record<string, number>> = { ...current };
    if (Object.keys(itemMap).length === 0) {
      delete next[itemId];
    } else {
      next[itemId] = itemMap;
    }

    updateField('mainServices', {
      ...state.mainServices,
      table: next,
    });
  };

  const msColumns = useMemo((): MSColumn[] => {
    const roadCols: MSColumn[] = roads.map((road) => ({
      title: road.roadName,
      key: `road_${road.id}`,
      width: 130,
      align: 'right' as const,
      render: (_: unknown, record: TableRow) => {
        if (record.rowType !== 'item') return null;
        const itemRow = record as ItemRow;
        return (
          <NumberInput
            value={getQuantity(itemRow.id, road.id)}
            onChange={(v) => updateQuantity(itemRow.id, road.id, v ?? 0)}
            decimalPlaces={3}
            positiveOnly
            placeholder="0.000"
            style={{ width: '100%', textAlign: 'right' }}
          />
        );
      },
    }));

    const totalCol: MSColumn = {
      title: 'Total Quant',
      key: 'total_quant',
      width: 130,
      align: 'right' as const,
      fixed: 'right' as const,
      render: (_: unknown, record: TableRow) => {
        if (record.rowType !== 'item') return null;
        const itemRow = record as ItemRow;
        const total = roads.reduce((sum, road) => sum + getQuantity(itemRow.id, road.id), 0);
        return <span style={{ fontWeight: 600 }}>{total.toFixed(3)}</span>;
      },
    };

    return [
      {
        title: 'Ref',
        dataIndex: 'ref',
        width: 70,
        align: 'right' as const,
        fixed: 'left' as const,
        render: (_: unknown, record: TableRow) => {
          if (record.rowType === 'section') return null;
          return (
            <span
              style={{
                color: '#000000',
                fontSize: 12,
                whiteSpace: 'nowrap',
                display: 'inline-block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'clip',
                verticalAlign: 'middle',
              }}
            >
              {(record as ItemRow).ref}
            </span>
          );
        },
      },
      {
        title: 'Description',
        dataIndex: 'description',
        width: 340,
        fixed: 'left' as const,
        render: (_: unknown, record: TableRow) => {
          if (record.rowType === 'section') {
            return (
              <Typography.Text strong className="text-indigo-600">
                {record.description}
              </Typography.Text>
            );
          }
          return <Typography.Text>{(record as ItemRow).description}</Typography.Text>;
        },
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        width: 70,
        fixed: 'left' as const,
        align: 'center' as const,
        render: (_: unknown, record: TableRow) => {
          if (record.rowType !== 'item') return null;
          const unit = (record as ItemRow).unit || '';
          return <span style={{ color: '#6b7280' }}>{formatUnit(unit)}</span>;
        },
      },
      ...(showDevMode ? [{
        title: 'Evaluate Code',
        key: 'evaluateCode',
        width: 140,
        fixed: 'left' as const,
        render: (_: unknown, record: TableRow) => {
          if (record.rowType !== 'item') return null;
          const code = findMainServiceTrenchingEvaluateCode((record as ItemRow).description);
          if (!code) return <Typography.Text type="secondary">—</Typography.Text>;
          const libRate = getLibRate(code);
          return (
            <span>
              <Tag color="blue">{code}</Tag>
              {libRate !== undefined && (
                <div style={{ color: '#52c41a', fontSize: 12, marginTop: 2 }}>
                  {formatCurrency(libRate)}
                </div>
              )}
            </span>
          );
        },
      }] : []),
      ...roadCols,
      totalCol,
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roads, savedQuantities, showDevMode, resourceCodeMap]);

  const handleMsImport = async (file: File) => {
    setMsImportLoading(true);
    try {
      const entries = await parseMainServicesTrenching(file);

      if (entries.length === 0) {
        alert('No matching items found in "Mains Services" sheet. Ensure the sheet exists and descriptions match exactly.');
        return;
      }

      const resolveRoadId = (roadIndex: number, roadHeaderName: string): string | undefined => {
        const byName = roads.find(
          (r) => r.roadName.trim().toLowerCase() === roadHeaderName.toLowerCase(),
        );
        if (byName) return byName.id;
        return roads[roadIndex]?.id;
      };

      const newQuantities: Record<string, Record<string, number>> = {};
      let matchCount = 0;

      entries.forEach(({ itemId, roadIndex, roadHeaderName, quantity }) => {
        const roadId = resolveRoadId(roadIndex, roadHeaderName);
        if (!roadId) return;
        if (!newQuantities[itemId]) newQuantities[itemId] = {};
        newQuantities[itemId][roadId] = quantity;
        matchCount++;
      });

      if (matchCount === 0) {
        alert('Quantities were found but could not be matched to any configured roads. Add roads in the Carriageway tab first.');
        return;
      }

      const current = state.mainServices?.table ?? {};
      const merged: Record<string, Record<string, number>> = { ...current };
      Object.entries(newQuantities).forEach(([itemId, roadMap]) => {
        merged[itemId] = { ...(merged[itemId] ?? {}), ...roadMap };
      });

      updateField('mainServices', {
        ...state.mainServices,
        table: merged,
      });

      setMsImportVisible(false);
      alert(`Import complete — ${matchCount} quantity value(s) updated.`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('Main Services import failed:', err);
      alert(`Import failed: ${msg}`);
    } finally {
      setMsImportLoading(false);
    }
  };

  // ── Substation Base data ───────────────────────────────────────────────────
  const savedSubstationData = useMemo(
    () => state.substation?.table ?? {},
    [state.substation?.table],
  );

  const getSubRow = (rowId: string): FoulBuildUpRowData =>
    savedSubstationData[rowId] ?? { quant: null, evaluateCode: '' };

  const updateSubRow = (rowId: string, changes: Partial<FoulBuildUpRowData>) => {
    const current = savedSubstationData[rowId] ?? { quant: null, evaluateCode: '' };
    updateField('substation', {
      ...state.substation,
      table: { ...savedSubstationData, [rowId]: { ...current, ...changes } },
    });
  };

  const savedSubstationConfig = (state.substation?.config ?? {}) as SubstationBaseConfig;
  const cartSpoilDistanceM = savedSubstationConfig.cartSpoilDistanceM ?? DEFAULT_CART_SPOIL_DISTANCE_M;
  const ssbBlindingMm = savedSubstationConfig.ssbBlindingMm ?? DEFAULT_SSB_BLINDING_MM;
  const ssbFormworkPerimeterM = savedSubstationConfig.ssbFormworkPerimeterM ?? DEFAULT_SSB_FORMWORK_PERIMETER_M;
  const ssbFormworkPitM = savedSubstationConfig.ssbFormworkPitM ?? DEFAULT_SSB_FORMWORK_PIT_M;
  const ssbFormworkVoidM = savedSubstationConfig.ssbFormworkVoidM ?? DEFAULT_SSB_FORMWORK_VOID_M;
  const ssbMeshType = savedSubstationConfig.ssbMeshType ?? DEFAULT_SSB_MESH_TYPE;
  const ssaBaseCourseLabel = savedSubstationConfig.ssaBaseCourseLabel ?? DEFAULT_SSA_BASE_COURSE;
  const ssaBinderCourseLabel = savedSubstationConfig.ssaBinderCourseLabel ?? DEFAULT_SSA_BINDER_COURSE;
  const ssaSurfaceCourseLabel = savedSubstationConfig.ssaSurfaceCourseLabel ?? DEFAULT_SSA_SURFACE_COURSE;

  const [sbConfigModalVisible, setSbConfigModalVisible] = useState(false);
  const [sbConfigDraft, setSbConfigDraft] = useState<SubstationBaseConfig>({
    cartSpoilDistanceM: DEFAULT_CART_SPOIL_DISTANCE_M,
    ssbBlindingMm: DEFAULT_SSB_BLINDING_MM,
    ssbFormworkPerimeterM: DEFAULT_SSB_FORMWORK_PERIMETER_M,
    ssbFormworkPitM: DEFAULT_SSB_FORMWORK_PIT_M,
    ssbFormworkVoidM: DEFAULT_SSB_FORMWORK_VOID_M,
    ssbMeshType: DEFAULT_SSB_MESH_TYPE,
    ssaBaseCourseLabel: DEFAULT_SSA_BASE_COURSE,
    ssaBinderCourseLabel: DEFAULT_SSA_BINDER_COURSE,
    ssaSurfaceCourseLabel: DEFAULT_SSA_SURFACE_COURSE,
  });

  const openSbConfigModal = () => {
    setSbConfigDraft({
      cartSpoilDistanceM: savedSubstationConfig.cartSpoilDistanceM ?? DEFAULT_CART_SPOIL_DISTANCE_M,
      ssbBlindingMm: savedSubstationConfig.ssbBlindingMm ?? DEFAULT_SSB_BLINDING_MM,
      ssbFormworkPerimeterM: savedSubstationConfig.ssbFormworkPerimeterM ?? DEFAULT_SSB_FORMWORK_PERIMETER_M,
      ssbFormworkPitM: savedSubstationConfig.ssbFormworkPitM ?? DEFAULT_SSB_FORMWORK_PIT_M,
      ssbFormworkVoidM: savedSubstationConfig.ssbFormworkVoidM ?? DEFAULT_SSB_FORMWORK_VOID_M,
      ssbMeshType: savedSubstationConfig.ssbMeshType ?? DEFAULT_SSB_MESH_TYPE,
      ssaBaseCourseLabel: savedSubstationConfig.ssaBaseCourseLabel ?? DEFAULT_SSA_BASE_COURSE,
      ssaBinderCourseLabel: savedSubstationConfig.ssaBinderCourseLabel ?? DEFAULT_SSA_BINDER_COURSE,
      ssaSurfaceCourseLabel: savedSubstationConfig.ssaSurfaceCourseLabel ?? DEFAULT_SSA_SURFACE_COURSE,
    });
    setSbConfigModalVisible(true);
  };

  const applySbConfig = () => {
    updateField('substation', { ...state.substation, config: { ...sbConfigDraft } });
    setSbConfigModalVisible(false);
  };

  const sbColumns = useMemo(() => [
    {
      title: 'Description',
      key: 'description',
      width: 480,
      fixed: 'left' as const,
      render: (_: unknown, record: SubstationTreeNode) => {
        if (record.rowType === 'sectionHeader') {
          return (
            <Typography.Text strong className="text-indigo-600">
              {record.description}
            </Typography.Text>
          );
        }
        const description =
          record.id === 'ssb_3' ? `Cart Spoil to onsite bund NE ${cartSpoilDistanceM > 0 ? cartSpoilDistanceM : 'xxx'}m` :
          record.id === 'ssb_4' ? `${ssbBlindingMm > 0 ? ssbBlindingMm : 'xx'}mm concrete Blinding` :
          record.id === 'ssb_5' ? `Formwork NE ${ssbFormworkPerimeterM > 0 ? ssbFormworkPerimeterM : 'x.x'}m to external perimeter` :
          record.id === 'ssb_6' ? `Formwork NE ${ssbFormworkPitM > 0 ? ssbFormworkPitM : 'x.xx'}m to form pit to frontage transformer` :
          record.id === 'ssb_7' ? `Formwork NE ${ssbFormworkVoidM > 0 ? ssbFormworkVoidM : 'x.x'}m to form walls void area under floor` :
          record.id === 'ssb_9' ? `Supply & Fix 14 sheets ${ssbMeshType && ssbMeshType.length > 0 ? ssbMeshType : 'xxx'} Mesh` :
          record.id === 'ssb_18' ? `Welding to ${ssbMeshType && ssbMeshType.length > 0 ? ssbMeshType : 'xxx'} Mesh and earthing rods (subby)` :
          record.id === 'ssa_2' ? (ssaBaseCourseLabel && ssaBaseCourseLabel.length > 0 ? ssaBaseCourseLabel : 'xxxmm thick AC 32mm dense bin') :
          record.id === 'ssa_3' ? (ssaBinderCourseLabel && ssaBinderCourseLabel.length > 0 ? ssaBinderCourseLabel : 'xxmm thick AC 20mm dense bin') :
          record.id === 'ssa_6' ? (ssaSurfaceCourseLabel && ssaSurfaceCourseLabel.length > 0 ? ssaSurfaceCourseLabel : 'xxmm thick AC 6mm dense surf') :
          record.description;
        return <Typography.Text>{description}</Typography.Text>;
      },
    },
    {
      title: 'Qty',
      key: 'qty',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: SubstationTreeNode) => {
        if (record.rowType !== 'data') return null;
        const row = getSubRow(record.id);
        return (
          <NumberInput
            value={row.quant ?? 0}
            onChange={(v) => updateSubRow(record.id, { quant: v })}
            decimalPlaces={3}
            positiveOnly
            placeholder="0.000"
            style={{ width: '100%', textAlign: 'right' }}
          />
        );
      },
    },
    {
      title: 'Unit',
      key: 'unit',
      width: 70,
      align: 'center' as const,
      render: (_: unknown, record: SubstationTreeNode) => {
        if (!record.unit) return null;
        return <span style={{ color: '#6b7280' }}>{formatUnit(record.unit)}</span>;
      },
    },
    ...(showDevMode ? [{
      title: 'Evaluate Code',
      key: 'evaluateCode',
      width: 160,
      render: (_: unknown, record: SubstationTreeNode) => {
        if (record.rowType !== 'data') return null;
        const code = findSubstationBaseEvaluateCode(record.description);
        if (!code) return <Typography.Text type="secondary">—</Typography.Text>;
        const libRate = getLibRate(code);
        return (
          <span>
            <Tag color="blue">{code}</Tag>
            {libRate !== undefined && (
              <div style={{ color: '#52c41a', fontSize: 12, marginTop: 2 }}>
                {formatCurrency(libRate)}
              </div>
            )}
          </span>
        );
      },
    }] : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [savedSubstationData, cartSpoilDistanceM, ssbBlindingMm, ssbFormworkPerimeterM, ssbFormworkPitM, ssbFormworkVoidM, ssbMeshType, ssaBaseCourseLabel, ssaBinderCourseLabel, ssaSurfaceCourseLabel, showDevMode, resourceCodeMap]);

  const handleSbImport = async (file: File) => {
    setSbImportLoading(true);
    try {
      const imported = await parseSubstationBaseFromExcel(file);
      updateField('substation', { ...state.substation, table: imported.table, config: imported.config });
      setSbImportVisible(false);
      const count = Object.keys(imported.table).length;
      if (count === 0) {
        alert('No matching rows found in "Substation Base" sheet. Check that the sheet exists and section headers are present.');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('Substation Base import failed:', err);
      alert(`Import failed: ${msg}`);
    } finally {
      setSbImportLoading(false);
    }
  };

  const handleSaveAndContinue = () => {
    updateField("status", "In-progress");
    updateField("boqStatus", "In-progress");
    updateField("lastModified", new Date());
    saveTender();
    navigate('/roads-sewers/pos');
  };

  const [activeTab, setActiveTab] = useState<'main-services' | 'substation-base'>('main-services');
  // Defers this step's tables' mount by one paint cycle so the tab doesn't
  // look frozen while the table's render/layout blocks the main thread -
  // see useTableRenderDeferral's own doc comment. Re-keyed on `activeTab`
  // since switching tabs here unmounts/mounts a brand new Table, not just a
  // dataSource update - so the loader should reappear on every tab switch,
  // not just the initial mount. Purely a perceived-lag fix: once it flips
  // false the tables render exactly as before.
  const isTableLoading = useTableRenderDeferral([activeTab]);

  const handleTabChange = (tab: 'main-services' | 'substation-base') => {
    setActiveTab(tab);
    setMsExpandedKeys([]);
    setSbExpandedKeys([]);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const calculateOffset = () => {
      const baseOffset = 144;
      const stickyHeader = document.getElementsByClassName('ms-sticky-header')?.[0];
      const height = stickyHeader?.getBoundingClientRect?.()?.height || 0;
      setStickyOffset(baseOffset + height);
    };
    calculateOffset();
    const timer = setTimeout(calculateOffset, 100);
    return () => clearTimeout(timer);
  }, [activeTab]);

  const msExpanded = msExpandedKeys.includes(MS_EXPAND_KEY);
  const sbAllExpanded = sbExpandedKeys.length === SUBSTATION_EXPAND_KEYS.length;

  // ── Tab content ────────────────────────────────────────────────────────────
  const mainServicesTab = (
    <div>
      {roads.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="No roads configured"
          description="Add roads in the Roads — Carriageway tab first. Road columns will appear here once roads are configured."
        />
      ) : (
        <Table<TableRow>
          bordered
          size="middle"
          scroll={{ x: 'max-content' }}
          sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
          pagination={false}
          dataSource={MS_TREE_DATA}
          rowKey="key"
          expandable={{
            childrenColumnName: 'children',
            expandedRowKeys: msExpandedKeys,
            onExpand: (expanded, record) => {
              const k = record.key as React.Key;
              setMsExpandedKeys((prev) =>
                expanded ? [...prev, k] : prev.filter((x) => x !== k),
              );
            },
          }}
          rowClassName={(record) => {
            if (record.rowType === 'section') return 'ms-row-section';
            return '';
          }}
          columns={msColumns}
        />
      )}
    </div>
  );

  const substationBaseTab = (
    <div>
      <Table<SubstationTreeNode>
        bordered
        size="middle"
        scroll={{ x: 900 }}
        sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
        pagination={false}
        dataSource={SUBSTATION_TREE}
        rowKey="key"
        expandable={{
          childrenColumnName: 'children',
          expandedRowKeys: sbExpandedKeys,
          onExpand: (expanded, record) => {
            const k = record.key as React.Key;
            setSbExpandedKeys((prev) =>
              expanded ? [...prev, k] : prev.filter((x) => x !== k),
            );
          },
        }}
        rowClassName={(record) => {
          if (record.rowType === 'sectionHeader') return 'sb-row-section';
          return '';
        }}
        columns={sbColumns}
      />
    </div>
  );

  return (
    <MainLayout showFooter={false}>
      <Card
        className="ms-card shadow-sm"
        styles={{ body: { padding: 0 } }}
        style={{ borderRadius: '8px', overflow: 'visible' }}
      >
        {/* Sticky header + tabs */}
        <div
          className="ms-sticky-header"
          style={{
            position: 'sticky',
            top: '144px',
            backgroundColor: '#fff',
            zIndex: 98,
            borderRadius: '8px 8px 0 0',
            isolation: 'isolate',
          }}
        >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex justify-between items-center" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <div>
            <Title level={3} style={{ fontWeight: 600, fontSize: '20px', color: '#111827', marginBottom: '4px' }}>
              Main Services &amp; Substation Base
            </Title>
            <Text type="secondary" style={{ fontSize: '14px', color: '#6B7280' }}>
              Main service trenching quantities and substation base works
            </Text>
          </div>

          {/* Configure / Import Take-off — flush right in the card header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {activeTab === 'substation-base' && (
              <Button
                size="small"
                icon={<SettingOutlined />}
                onClick={openSbConfigModal}
                type="default"
                className="configure-headers-btn"
                style={{
                  height: '32px',
                  borderRadius: '6px',
                  fontWeight: 500,
                  borderColor: '#1890ff',
                  color: '#1890ff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginTop:'16px'
                }}
              >
                Configure
              </Button>
            )}
            {showDevMode && activeTab === 'main-services' && (
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={() => setMsImportVisible(true)}
                style={{ color: '#1890ff', fontWeight: 500 }}
              >
                Import Take-off
              </Button>
            )}
            {showDevMode && activeTab === 'substation-base' && (
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={() => setSbImportVisible(true)}
                style={{ color: '#1890ff', fontWeight: 500 }}
              >
                Import Take-off
              </Button>
            )}
          </div>
        </div>

        {/* Tab Buttons */}
        <div className="ms-sticky-tabs px-6" style={{ paddingTop: '16px', paddingBottom: '0px', display: 'flex', gap: '4px', alignItems: 'flex-end', backgroundColor: '#fff' }}>
          {([
            { key: 'main-services',   label: 'Main Services' },
            { key: 'substation-base', label: 'Substation Base' },
          ] as const).map((tab) => (
            <Button
              key={tab.key}
              type={activeTab === tab.key ? 'primary' : 'default'}
              className={activeTab === tab.key ? 'active-tab-button' : 'inactive-tab-button'}
              onClick={() => handleTabChange(tab.key)}
              style={{
                backgroundColor: activeTab === tab.key ? COLORS.primary : '#E1E6EF',
                borderColor: activeTab === tab.key ? COLORS.primary : '#E1E6EF',
                color: activeTab === tab.key ? '#fff' : '#6B7280',
                fontWeight: 500,
                borderRadius: '6px 6px 0 0',
                height: '38px',
                paddingLeft: '20px',
                paddingRight: '20px',
                borderBottom: activeTab === tab.key ? 'none' : '1px solid #E1E6EF',
                position: 'relative',
                zIndex: activeTab === tab.key ? 2 : 1,
              }}
            >
              {tab.label}
            </Button>
          ))}

          {/* Tab-level action buttons — flush right */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 4 }}>
            {activeTab === 'main-services' && (
              <ExpandCollapseAllButton
                expanded={msExpanded}
                onToggle={() => setMsExpandedKeys(msExpanded ? [] : [MS_EXPAND_KEY])}
              />
            )}
            {activeTab === 'substation-base' && (
              <ExpandCollapseAllButton
                expanded={sbAllExpanded}
                onToggle={() => setSbExpandedKeys(sbAllExpanded ? [] : SUBSTATION_EXPAND_KEYS)}
              />
            )}
          </div>
        </div>
        </div>{/* end ms-sticky-header */}

        {/* Tab Content */}
        <div className="px-6 pb-6 ms-content-section" style={{ paddingTop: '0px', position: 'relative', zIndex: 1 }}>
          {activeTab === 'main-services' && <div >{isTableLoading ? <TableLoadingPlaceholder /> : mainServicesTab}</div>}
          {activeTab === 'substation-base' && <div >{isTableLoading ? <TableLoadingPlaceholder /> : substationBaseTab}</div>}
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #e5e7eb' }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{ backgroundColor: COLORS.primary, borderColor: COLORS.primary }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      <ExcelImportModal
        onImport={handleMsImport}
        onDownloadTemplate={() => {/* template not required */}}
        importLoading={msImportLoading}
        importModalVisible={msImportVisible}
        setImportModalVisible={setMsImportVisible}
      />

      <ExcelImportModal
        onImport={handleSbImport}
        onDownloadTemplate={() => {/* template not required */}}
        importLoading={sbImportLoading}
        importModalVisible={sbImportVisible}
        setImportModalVisible={setSbImportVisible}
      />

      <ColumnConfigModal
        title="Configure"
        open={sbConfigModalVisible}
        onClose={() => setSbConfigModalVisible(false)}
        onApply={applySbConfig}
        width={640}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="Sub Station Base" columns={2}>
          <ConfigField label="Cart Spoil to onsite bund NE xxxm">
            <NumberInput
              value={sbConfigDraft.cartSpoilDistanceM ?? DEFAULT_CART_SPOIL_DISTANCE_M}
              onChange={(v) => setSbConfigDraft((prev) => ({ ...prev, cartSpoilDistanceM: v ?? DEFAULT_CART_SPOIL_DISTANCE_M }))}
              decimalPlaces={1}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="xxmm concrete Blinding">
            <NumberInput
              value={sbConfigDraft.ssbBlindingMm ?? DEFAULT_SSB_BLINDING_MM}
              onChange={(v) => setSbConfigDraft((prev) => ({ ...prev, ssbBlindingMm: v ?? DEFAULT_SSB_BLINDING_MM }))}
              decimalPlaces={0}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Formwork NE x.xm to external perimeter">
            <NumberInput
              value={sbConfigDraft.ssbFormworkPerimeterM ?? DEFAULT_SSB_FORMWORK_PERIMETER_M}
              onChange={(v) => setSbConfigDraft((prev) => ({ ...prev, ssbFormworkPerimeterM: v ?? DEFAULT_SSB_FORMWORK_PERIMETER_M }))}
              decimalPlaces={2}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Formwork NE x.xxm to form pit to frontage transformer">
            <NumberInput
              value={sbConfigDraft.ssbFormworkPitM ?? DEFAULT_SSB_FORMWORK_PIT_M}
              onChange={(v) => setSbConfigDraft((prev) => ({ ...prev, ssbFormworkPitM: v ?? DEFAULT_SSB_FORMWORK_PIT_M }))}
              decimalPlaces={2}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Formwork NE x.xm to form walls void area under floor">
            <NumberInput
              value={sbConfigDraft.ssbFormworkVoidM ?? DEFAULT_SSB_FORMWORK_VOID_M}
              onChange={(v) => setSbConfigDraft((prev) => ({ ...prev, ssbFormworkVoidM: v ?? DEFAULT_SSB_FORMWORK_VOID_M }))}
              decimalPlaces={2}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Mesh Reinforcement">
            <select
              value={sbConfigDraft.ssbMeshType ?? DEFAULT_SSB_MESH_TYPE}
              onChange={(e) => setSbConfigDraft((prev) => ({ ...prev, ssbMeshType: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {MESH_TYPE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
        </ConfigSection>
        <ConfigSection title="Sub Station Access" columns={2}>
          <ConfigField label="xxxmm Base Course">
            <select
              value={sbConfigDraft.ssaBaseCourseLabel ?? DEFAULT_SSA_BASE_COURSE}
              onChange={(e) => setSbConfigDraft((prev) => ({ ...prev, ssaBaseCourseLabel: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {SSA_BASE_COURSE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label="xxmm Binder Course">
            <select
              value={sbConfigDraft.ssaBinderCourseLabel ?? DEFAULT_SSA_BINDER_COURSE}
              onChange={(e) => setSbConfigDraft((prev) => ({ ...prev, ssaBinderCourseLabel: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {SSA_BINDER_COURSE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label="xxmm Surface Course">
            <select
              value={sbConfigDraft.ssaSurfaceCourseLabel ?? DEFAULT_SSA_SURFACE_COURSE}
              onChange={(e) => setSbConfigDraft((prev) => ({ ...prev, ssaSurfaceCourseLabel: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {SSA_SURFACE_COURSE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
        </ConfigSection>
      </ColumnConfigModal>

      <style>{`
        .ms-card {
          overflow: visible !important;
        }
        .ms-card .ant-card-body {
          overflow: visible !important;
        }
        .ms-card .ant-table-tbody > tr > td {
          vertical-align: middle;
        }
        .ms-sticky-header {
          box-shadow: none;
          will-change: transform;
          transform: translateZ(0);
          z-index: 98 !important;
        }
        .ms-sticky-tabs {
          border-bottom: none;
          min-height: 40px;
        }
        .ms-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }
        .active-tab-button {
          border-bottom: none !important;
        }
        .inactive-tab-button {
          border-bottom: none !important;
        }
        .ms-row-section > td {
          background-color: #e0e7ff !important;
          font-weight: 700;
        }
        .sb-row-section > td {
          background-color: #e0e7ff !important;
          font-weight: 700;
        }
        .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
        }
      `}</style>
    </MainLayout>
  );
};

export default MainServices;
